/**
 * Which Spotify Connect device should play. Structural on purpose so the
 * domain rule stays testable without the Spotify client.
 */
export interface DeviceCandidate {
  id: string | null
  /** Spotify device type, e.g. `Smartphone`, `Computer`, `Speaker`. */
  type: string
  isActive: boolean
  isRestricted: boolean
}

/** Device types that correspond to a native Spotify app rather than a speaker. */
const NATIVE_APP_TYPES = new Set(['smartphone', 'tablet'])

export function isNativeAppDevice(device: DeviceCandidate): boolean {
  return NATIVE_APP_TYPES.has(device.type.toLowerCase())
}

/**
 * Preference order, per spec: the native Spotify app wins because it survives
 * screen lock and app switching mid-party; our own in-browser SDK device is the
 * fallback of last resort.
 */
export function pickPreferredDevice<T extends DeviceCandidate>(
  devices: readonly T[],
  ownSdkDeviceId: string | null = null,
): T | null {
  const usable = devices.filter((device) => device.id !== null && !device.isRestricted)
  const external = usable.filter((device) => device.id !== ownSdkDeviceId)

  return (
    external.find((device) => device.isActive && isNativeAppDevice(device)) ??
    external.find((device) => isNativeAppDevice(device)) ??
    external.find((device) => device.isActive) ??
    external[0] ??
    usable.find((device) => device.id === ownSdkDeviceId) ??
    null
  )
}
